import {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {DeleteOutlined, InfoCircleOutlined, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {TweenOneGroup} from 'rc-tween-one';
import {useStore} from "../contexts/AppContext";
import {
    Button,
    Col,
    DatePicker,
    Form,
    Input,
    message,
    Modal,
    Radio,
    Row,
    Select,
    Tag,
    theme,
    Tooltip,
    TreeSelect,
} from "antd";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import api from "../http";
import {customFieldRequestForm} from "../utils/constants";
import {modalThemeConstants} from "../utils/modalThemeConstants";

const {RangePicker} = DatePicker;
const {SHOW_PARENT} = TreeSelect;


const DocumentInfo = forwardRef(({isIamViewer, isAccessDenied}, ref) => {
        const {
            documentTypes, setDocumentTypes, brands, periods, countries, advertiseResources,
            department, row, setRow,
        } = useStore()
        const [form] = Form.useForm();
        const [customFieldForm] = Form.useForm();
        const {token} = theme.useToken();
        const [inputVisible, setInputVisible] = useState(false);
        const [inputValue, setInputValue] = useState('');
        const inputRef = useRef(null);
        const [refreshingPublic, setRefreshingPublic] = useState(false);
        const [customFieldRequest, setCustomFieldRequest] = useState(customFieldRequestForm)
        const [searchCustomField, setSearchCustomField] = useState("")
        const [isModalOpen, setIsModalOpen] = useState(false);


        useEffect(() => {
            if (row && form) {
                form.setFieldsValue({
                    title: row.title || '',
                    countries_id_array: row.countries_id_array || [],
                    document_url: row.document_url || '',
                    adv_res_id_array: row.adv_res_id_array || [],
                    document_type_id: row.document_type_id === 0 ? "0" : row.document_type_id || null,
                    department_id: row.department_id || null,
                    brand_id: row.brand_id || null,
                    period_type_id: row.period_type_id === 0 ? 100 : row.period_type_id || null,
                    period_year: row.date_start
                        ? new Date(row.date_start).getFullYear()
                        : new Date().getFullYear(),
                    period_id: row.period_id || null,
                    dates: [
                        row.date_start
                            ? dayjs(row.date_start)
                            : dayjs(new Date(new Date().getFullYear(), 0, 1, 12)),
                        row.date_end
                            ? dayjs(row.date_end)
                            : dayjs(new Date(new Date().getFullYear(), 11, 31, 12))
                    ],
                    description: row.description || ''
                });
            }
        }, [row, form]);

        useEffect(() => {
            if (inputVisible) {
                inputRef.current?.focus();
            }
        }, [inputVisible]);


        useImperativeHandle(ref, () => ({
            submit: () => form.submit(),
            validateFields: () => form.validateFields(),
        }));



        const years = Array.from({length: 31}, (_, i) => {
            const y = new Date().getFullYear() + 5 - i;
            return {label: y.toString(), value: y};
        });


        const buildNested = (data, mode, parentId = null, prefix = "0", level = 0, path = []) => {
            const nestedItems = data.filter((item) => item.parent_id === parentId);

            return nestedItems.map((item) => {
                const currentPath = [...path, {title: item.title.trim()}];
                const breadcrumbStr = currentPath.map(p => p.title).join(" / ");

                const children = buildNested(data, mode, item.id, item.id, level + 1, currentPath);
                let result

                if (mode === "multi") {
                    result = {
                        title: item.title.trim(),
                        value: item.id,
                        key: item.id,
                        breadcrumb: breadcrumbStr,
                    };
                } else if (mode === "one") {
                    result = {
                        title: item.title.trim(),
                        value: item.id,
                        breadcrumb: breadcrumbStr,
                    };
                }

                if (children.length > 0) {
                    result.children = children;
                }

                return result;
            });
        };


        const findRootId = (id, list) => {
            let current = list.find(c => c.id === id);

            if (!current) return null;

            while (current.parent_id !== null) {
                current = list.find(c => c.id === current.parent_id);
            }

            return current.id;
        };


        const countriesChange = (newValues) => {
            const last = newValues[newValues.length - 1];

            const lastRoot = findRootId(last, countries);
            const filtered = newValues.filter(id => findRootId(id, countries) === lastRoot);

            setRow(prev => ({
                ...prev,
                countries_id_array: filtered
            }));
        };


        const advChange = (newValues) => {
            const last = newValues[newValues.length - 1];
            const lastRoot = findRootId(last, advertiseResources);
            const filtered = newValues.filter(id => findRootId(id, advertiseResources) === lastRoot);

            setRow(prev => ({
                ...prev,
                adv_res_id_array: filtered
            }));
        };

        const docTypeChange = (value) => {
            setRow((prevState) => ({
                ...prevState,
                document_type_id: value === 0 ? "0" : value,
            }));
        };


        const tCountryProps = {
            treeData: buildNested(countries, "multi"),
            value: row?.countries_id_array || [],
            onChange: countriesChange,
            treeCheckable: true,
            showCheckedStrategy: SHOW_PARENT,
            placeholder: "Please select countries",
            style: {
                width: "100%",
            },
        };
        const tAdvProps = {
            treeData: buildNested(advertiseResources, "multi"),
            value: row?.countries_id_array || [],
            onChange: advChange,
            treeCheckable: true,
            showCheckedStrategy: SHOW_PARENT,
            placeholder: "Please select advertise",
            style: {
                width: "100%",
            },
        };

        const tDocTypeProps = {
            treeData: buildNested(documentTypes, "one"),
            value: row?.document_type_id || null,
            onChange: docTypeChange,
            treeCheckable: false,
            showSearch: true,
            showCheckedStrategy: SHOW_PARENT,
            placeholder: "Please select doc type",
            style: {
                width: '100%',
                popup: {root: {maxHeight: 400, overflow: 'auto'}},
            },
        };

        const onChangePeriod = (key) => {
            const value = key.target.value
            const year = row.date_start ? new Date(row.date_start).getFullYear() : new Date().getFullYear();

            if (value === 100) {
                setRow(prevState => ({
                    ...prevState,
                    period_type_id: 0,
                    period_id: 0,
                    date_start: new Date(2000, 0, 1, 12),
                    date_end: new Date(2100, 11, 31, 12)
                }));
            } else {
                const checkYear = year !== 2000 ? year : new Date().getFullYear()
                setRow(prevState => ({
                    ...prevState,
                    period_type_id: value,
                    period_id: value,
                    date_start: new Date(checkYear, 0, 1, 12),
                    date_end: new Date(checkYear, 11, 31, 12)
                }));
            }
        }

        const handleSelectYear = (year) => {
            let start
            let end
            if (row.date_start && row.date_end) {
                const dateStart = new Date(row.date_start)
                const dateEnd = new Date(row.date_end)
                start = new Date(year, dateStart.getMonth(), dateStart.getDate(), 12)
                end = new Date(year, dateEnd.getMonth(), dateEnd.getDate(), 12)
            } else {
                start = new Date(year, 0, 1, 12);
                end = new Date(year, 11, 31, 12);
            }

            setRow(prevState => ({
                ...prevState,
                date_start: start,
                date_end: end,
                period_id: 1
            }));
        };

        const handleSelectQuarter = (quarterId) => {
            let start
            let end
            const quarters = {
                2: [0, 2],
                3: [3, 5],
                4: [6, 8],
                5: [9, 11]
            };

            if (quarterId) {
                const year = row.date_start ? new Date(row.date_start).getFullYear() : new Date().getFullYear();
                const [startMonth, endMonth] = quarters[quarterId];
                start = new Date(year, startMonth, 1, 12);
                end = new Date(year, endMonth + 1, 0, 12);
            }
            setRow(prevState => ({
                ...prevState,
                date_start: start,
                date_end: end,
                period_id: quarterId,
            }))
        };

        const handleSelectMonth = (monthId) => {
            let start
            let end
            if (monthId) {
                const month = periods.find(per => per.id === Number(row?.period_type_id)).documents_periods.find(period => period.period_id === monthId)
                const year = row.date_start ? new Date(row.date_start).getFullYear() : new Date().getFullYear();
                start = new Date(year, month.period_queue - 1, 1, 12);
                end = new Date(year, month.period_queue, 0, 12);
            }
            setRow(prevState => ({
                ...prevState,
                date_start: start,
                date_end: end,
                period_id: monthId,
            }))
        };


        const handleClose = removedTag => {
            const newTags = row.tags_array.filter(tag => tag !== removedTag);
            setRow(prevState => ({
                ...prevState,
                tags_array: newTags
            }));
        };
        const showInput = () => {
            setInputVisible(true);
        };
        const handleInputChange = e => {
            setInputValue(e.target.value);
        };
        const handleInputConfirm = () => {
            if (inputValue && !row.tags_array.includes(inputValue)) {
                setRow(prevState => ({
                    ...prevState,
                    tags_array: [...(prevState.tags_array || []), inputValue]
                }));
            }
            setInputVisible(false);
            setInputValue('');
        };
        const forMap = tag => (
            <span key={tag} style={{display: 'inline-block'}}>
      <Tag
          closable={!isIamViewer || isAccessDenied}
          onClose={e => {
              e.preventDefault();
              handleClose(tag);
          }}
      >
        {tag}
      </Tag>
    </span>
        );
        const tagChild = row.tags_array.map(forMap) || [];
        const tagPlusStyle = {
            background: token.colorBgContainer,
            borderStyle: 'dashed',
        };

        const isPublicCheck = async () => {
            if (!row?.document_url || !row?.id) return;
            setRefreshingPublic(true);
            try {
                const resp = await api.post('supabase/manual-recheck-document-public', {
                    url: row.document_url,
                    documentId: row.id
                })
                if (resp?.data?.success) {
                    const {is_public, message} = resp.data;
                    setRow(prev => ({...prev, is_public}));

                    if (!is_public) {
                        console.log(message)
                    }
                }
            } catch (e) {
                console.log(e)
            } finally {
                setRefreshingPublic(false);
            }
        }

        const onCustomFieldsRequestOk = async () => {
            try {
                const values = await customFieldForm.validateFields();
                const document_type_title = documentTypes.find(doc => doc.id === values.document_type_id).title
                const response = await api.post('support/custom-fields', {...values, document_type_title})
                if (response.status === 200) {
                    message.success(response.data.message, 5)
                    setDocumentTypes(prev => [...prev, response.data.data]);
                    setRow((prevState) => ({
                        ...prevState,
                        document_type_id: response.data.data.id,
                    }));
                    onCustomFieldsClose()
                }
            } catch (err) {
                message.error(err.message || "Fill in the required fields");
            }
        }

        const onCustomFieldsClose = () => {
            customFieldForm.setFieldsValue(customFieldRequestForm)
            setIsModalOpen(false);
        }


        return (
            <>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        title: row.title || '',
                        countries_id_array: row.countries_id_array || [],
                        document_url: row.document_url || '',
                        adv_res_id_array: row.adv_res_id_array || [],
                        document_type_id: row.document_type_id || null,
                        department_id: row.department_id || null,
                        brand_id: row.brand_id || null,
                        period_type_id: String(row.period_type_id) || "",
                        period_year: row.date_start ? new Date(row.date_start).getFullYear() : new Date().getFullYear(),
                        period_id: row.period_id || 1,
                        dates: [dayjs(row.date_start), dayjs(row.date_end)],
                        description: row.description || ''
                    }}
                >
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="title"
                                label="Title"
                            >
                                <Input
                                    style={{width: '100%'}}
                                    onChange={(e) =>
                                        setRow((prevState => ({
                                            ...prevState,
                                            title: e.target.value
                                        })))
                                    }
                                    placeholder="Please enter title"
                                    disabled={isIamViewer || isAccessDenied}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="brand_id"
                                label="Brands"
                                rules={[{required: true, message: 'Please choose brands'}]}
                            >
                                <Select
                                    style={{width: "100%"}}
                                    value={row.brand_id}
                                    onChange={(e) => setRow(prevState => ({
                                        ...prevState,
                                        brand_id: e
                                    }))}
                                    options={brands.map(d => ({
                                        value: d.id,
                                        label: d.title
                                    }))}
                                    disabled={isIamViewer || isAccessDenied}
                                    placeholder='Please choose brand'
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    showSearch
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="department_id"
                                label="Departments"
                                rules={[{required: true, message: 'Please choose department'}]}
                            >
                                <Select
                                    style={{width: "100%"}}
                                    onChange={(e) => setRow(prevState => ({
                                        ...prevState,
                                        department_id: e
                                    }))}
                                    options={department.map(d => ({
                                        value: d.id,
                                        label: d.title
                                    }))}
                                    disabled={isIamViewer || isAccessDenied}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    placeholder='Please choose department'
                                    showSearch
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name="countries_id_array"
                                label="Countries"
                                rules={[{required: true, message: 'Please choose countries'}]}
                            >
                                <TreeSelect
                                    {...tCountryProps}
                                    disabled={isIamViewer || isAccessDenied}
                                    filterTreeNode={(input, treeNode) =>
                                        treeNode.title.toLowerCase().includes(input.toLowerCase())
                                    }
                                    treeNodeLabelProp="breadcrumb"
                                />
                            </Form.Item>

                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name="adv_res_id_array"
                                label="Advertise resources"
                                rules={[{required: true, message: 'Please choose advertise resources'}]}
                            >
                                <TreeSelect
                                    {...tAdvProps}
                                    disabled={isIamViewer || isAccessDenied}
                                    filterTreeNode={(input, treeNode) =>
                                        treeNode.title.toLowerCase().includes(input.toLowerCase())
                                    }
                                    treeNodeLabelProp="breadcrumb"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name="document_type_id"
                                label="Document types"
                                rules={[{required: true, message: 'Please choose document type'}]}
                            >
                                <TreeSelect
                                    {...tDocTypeProps}
                                    disabled={isIamViewer || isAccessDenied}
                                    filterTreeNode={(input, treeNode) =>
                                        treeNode.title.toLowerCase().includes(input.toLowerCase())
                                    }
                                    treeNodeLabelProp="breadcrumb"
                                    onSearch={(value) => setSearchCustomField(value)}
                                    suffixIcon={
                                        searchCustomField && (
                                            <Button
                                                type="text"
                                                icon={<PlusOutlined/>}
                                                size="small"
                                                disabled={isIamViewer}
                                                onClick={() => {
                                                    setCustomFieldRequest(prevState => ({
                                                        ...prevState,
                                                        title: searchCustomField
                                                    }))
                                                    setIsModalOpen(true)
                                                }}
                                            />
                                        )
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name="document_url"
                                label="Url"
                                rules={[{required: true, message: 'Please enter url'}]}
                            >
                                <Input
                                    placeholder="Please enter url"
                                    style={{width: '100%'}}
                                    disabled={isIamViewer || isAccessDenied}
                                    onChange={(e) =>
                                        setRow((prev) => ({...prev, document_url: e.target.value}))
                                    }
                                    suffix={
                                        <>
                                            <div
                                                title={row?.is_public === true ? 'Public: not secure' : row?.is_public === false ? 'Private: protected' : 'Unknown: not checked'}>
                                                <span style={{fontSize: 16}}>
                                                    {row?.is_public === true && '🚫'}
                                                    {row?.is_public === false && '🛡️'}
                                                    {row?.is_public !== true && row?.is_public !== false && '🌐'}
                                                </span>
                                            </div>
                                            <Button
                                                type="text"
                                                icon={<ReloadOutlined/>}
                                                size="small"
                                                loading={refreshingPublic}
                                                disabled={isIamViewer || isAccessDenied || !row?.document_url}
                                                onClick={isPublicCheck}
                                            />
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined/>}
                                                size="small"
                                                danger
                                                disabled={isIamViewer || isAccessDenied}
                                                onClick={() => setRow((prev => ({...prev, document_url: ""})))}
                                            />
                                        </>
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name="period_type_id"
                                label="Periods"
                                rules={[{required: true, message: 'Please choose period'}]}
                            >
                                <Radio.Group
                                    name="radiogroup"
                                    onChange={onChangePeriod}
                                    options={[
                                        {value: 100, label: 'Бессрочный'},
                                        {value: 1, label: 'Годовой'},
                                        {value: 2, label: 'Квартальный'},
                                        {value: 3, label: 'Месячный'},
                                    ]}
                                    disabled={isIamViewer || isAccessDenied}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item
                                name="dates"
                                label="Dates"
                            >
                                <RangePicker disabled/>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name="period_year"
                                label="Periods"
                                rules={[{required: true, message: 'Please choose year'}]}
                            >
                                <Select
                                    style={{width: "100%"}}
                                    onChange={handleSelectYear}
                                    options={years.map(d => ({value: d.value, label: d.label}))}
                                    disabled={isIamViewer || isAccessDenied || row.period_id === 0}
                                />
                            </Form.Item>
                        </Col>

                        {row?.period_type_id === 2 && (
                            <Col span={5}>
                                <Form.Item
                                    name="period_id"
                                    label="Quarter"
                                    rules={[{required: true, message: 'Please choose quarter'}]}
                                >
                                    <Select
                                        style={{width: "100%"}}
                                        onChange={handleSelectQuarter}
                                        options={
                                            periods.find(per => per.id === row?.period_type_id)?.documents_periods.map(item => ({
                                                label: item.period_title,
                                                value: item.period_id,
                                            })) || []
                                        }
                                        disabled={isIamViewer || isAccessDenied}
                                    />
                                </Form.Item>
                            </Col>
                        )}

                        {row?.period_type_id === 3 && (
                            <Col span={5}>
                                <Form.Item
                                    name="period_id"
                                    label="Month"
                                    rules={[{required: true, message: 'Please choose month'}]}
                                >
                                    <Select
                                        style={{width: "100%"}}
                                        onChange={handleSelectMonth}
                                        options={
                                            periods.find(per => per.id === row?.period_type_id)?.documents_periods.map(item => ({
                                                label: item.period_title,
                                                value: item.period_id,
                                            })) || []
                                        }
                                        disabled={isIamViewer || isAccessDenied}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        <Col span={24}>
                            <Form.Item
                                name="description"
                                label="Description"
                            >
                                <TextArea
                                    rows={3}
                                    onChange={(e) =>
                                        setRow((prevState => ({
                                            ...prevState,
                                            description: e.target.value
                                        })))
                                    }
                                    placeholder='Please enter text'
                                    disabled={isIamViewer || isAccessDenied}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                <>
                    <div style={{marginBottom: 16}}>
                        <TweenOneGroup
                            appear={false}
                            enter={{scale: 0.8, opacity: 0, type: 'from', duration: 100}}
                            leave={{opacity: 0, width: 0, scale: 0, duration: 200}}
                            onEnd={e => {
                                if (e.type === 'appear' || e.type === 'enter') {
                                    e.target.style = 'display: inline-block';
                                }
                            }}

                        >
                            {tagChild}
                        </TweenOneGroup>
                    </div>
                    {inputVisible ? (
                        <Input
                            ref={inputRef}
                            type="text"
                            size="small"
                            style={{width: 78}}
                            value={inputValue}
                            onChange={handleInputChange}
                            onBlur={handleInputConfirm}
                            onPressEnter={handleInputConfirm}
                            disabled={isIamViewer || isAccessDenied}
                        />
                    ) : (
                        <Tag onClick={showInput} style={tagPlusStyle}>
                            <PlusOutlined/> New Tag
                        </Tag>
                    )}
                </>
                <Modal
                    styles={modalThemeConstants(token)}
                    title="Form for requesting custom fields"
                    closable={{'aria-label': 'Custom Close Button'}}
                    open={isModalOpen}
                    onCancel={onCustomFieldsClose}
                    onOk={onCustomFieldsRequestOk}
                >
                    <Form
                        form={customFieldForm}
                        layout="vertical"
                        initialValues={customFieldRequest}
                    >
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="title"
                                    label="Name of type"
                                    rules={[{required: true, message: 'Please put title'}]}
                                >
                                    <Input
                                        placeholder="Put title"
                                        style={{width: "100%"}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="document_type_id"
                                    label="Document types"
                                    rules={[{required: true, message: 'Please choose document type'}]}
                                >
                                    <TreeSelect
                                        {...tDocTypeProps}
                                        disabled={isIamViewer || isAccessDenied}
                                        filterTreeNode={(input, treeNode) =>
                                            treeNode.title.toLowerCase().includes(input.toLowerCase())
                                        }
                                        suffixIcon={
                                            <Tooltip title="Select the folder where you want to create your document type">
                                                <InfoCircleOutlined/>
                                            </Tooltip>
                                        }
                                        treeNodeLabelProp="breadcrumb"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="context_of_use"
                                    label="Сontext of use"
                                    rules={[{required: true, message: 'Please put context of use'}]}
                                >
                                    <Input
                                        placeholder="Put context of use"
                                        style={{width: "100%"}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="variations"
                                    label="Title Variations separated commmas"
                                >
                                    <Input
                                        placeholder="Put variations"
                                        style={{width: "100%"}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="priority"
                                    label="Priority"
                                    rules={[{required: true, message: 'Please choose priority'}]}
                                >
                                    <Select
                                        style={{width: "100%"}}
                                        options={[
                                            {value: 'standard', label: <span>Standard</span>},
                                            {value: 'urgently', label: <span>Urgently</span>},
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </>
        )
    }
)

export default DocumentInfo;